import { useState, useEffect } from "react";
import {
  WalletOutlined, ThunderboltOutlined, ApiOutlined, FileTextOutlined,
  TeamOutlined, CheckSquareOutlined, LoginOutlined, PlusCircleOutlined,
  SolutionOutlined, UsergroupAddOutlined,
} from "@ant-design/icons";
import WalletTab from "./WalletTab";
import AiCreditsTab from "./AiCreditsTab";
import AiUsageTab from "./AiUsageTab";
import NoticeRepliesTab from "./NoticeRepliesTab";
import TeamTab from "./TeamTab";
import TasksTab from "./TasksTab";
import LoginActivityTab from "./LoginActivityTab";
import TmBreakdownTab from "./TmBreakdownTab";
import ClientsTab from "./ClientsTab";
import ClientGroupsTab from "./ClientGroupsTab";

/**
 * The account panels, behind one tab instead of eight.
 *
 * Adding these to the main bar took it to nineteen tabs, and antd answered by
 * folding half of them into a "..." menu — the new panels were the ones that
 * disappeared. They also ask a different question from the rest of the screen:
 * the outer tabs are about a trademark portfolio, these are about the account
 * that pays for it. So they get one outer tab and their own row inside it.
 *
 * A segmented row rather than nested antd Tabs: two identical tab bars stacked
 * on top of each other is hard to read as a hierarchy.
 */

const PANELS = [
  { key: "wallet", label: "Wallet", icon: <WalletOutlined />, Panel: WalletTab, badge: (s) => null },
  { key: "ai-credits", label: "AI Credits", icon: <ThunderboltOutlined />, Panel: AiCreditsTab, badge: (s) => s?.ai_credits?.balance },
  { key: "ai-usage", label: "AI Usage", icon: <ApiOutlined />, Panel: AiUsageTab, badge: () => null },
  { key: "notice-replies", label: "Notice Replies", icon: <FileTextOutlined />, Panel: NoticeRepliesTab, badge: (s) => s?.notice_replies },
  { key: "clients", label: "Clients", icon: <SolutionOutlined />, Panel: ClientsTab, badge: (s) => s?.clients },
  { key: "client-groups", label: "Client Groups", icon: <UsergroupAddOutlined />, Panel: ClientGroupsTab, badge: (s) => s?.client_groups },
  { key: "team", label: "Team & Roles", icon: <TeamOutlined />, Panel: TeamTab, badge: (s) => s?.team_members },
  { key: "tasks", label: "Tasks", icon: <CheckSquareOutlined />, Panel: TasksTab, badge: (s) => s?.open_tasks },
  { key: "logins", label: "Login Activity", icon: <LoginOutlined />, Panel: LoginActivityTab, badge: () => null },
  { key: "tm-breakdown", label: "TM Breakdown", icon: <PlusCircleOutlined />, Panel: TmBreakdownTab, badge: (s) => s?.total_tm_manual },
];

const AccountTabs = ({ userId, summary, active, openPanel }) => {
  const [panel, setPanel] = useState("wallet");

  /* A summary card can point straight at one of these. The outer tab switches to
     Account and names the panel it meant; without this the click would land on
     Wallet whatever was asked for. */
  useEffect(() => {
    if (openPanel && PANELS.some((p) => p.key === openPanel)) setPanel(openPanel);
  }, [openPanel]);

  return (
    <div>
      <div style={{
        display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20,
        padding: 6, background: "#fff", borderRadius: 14,
        border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}>
        {PANELS.map((p) => {
          const on = panel === p.key;
          const count = p.badge(summary);
          return (
            <button
              key={p.key}
              onClick={() => setPanel(p.key)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "8px 15px", borderRadius: 10, border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: on ? 700 : 500, whiteSpace: "nowrap",
                background: on ? "#eef2ff" : "transparent",
                color: on ? "#4f46e5" : "#64748b",
                transition: "background .15s ease, color .15s ease",
              }}
            >
              <span style={{ fontSize: 14 }}>{p.icon}</span>
              {p.label}
              {count > 0 && (
                <span style={{
                  padding: "1px 7px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                  background: on ? "#4f46e5" : "#e2e8f0",
                  color: on ? "#fff" : "#64748b",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {Number(count).toLocaleString("en-IN")}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Mounted but inert until selected — each panel only fetches when its own
          `active` turns true, and keeps its rows when you move away and back. */}
      {PANELS.map(({ key, Panel }) => (
        <div key={key} style={{ display: panel === key ? "block" : "none" }}>
          <Panel userId={userId} active={active && panel === key} />
        </div>
      ))}
    </div>
  );
};

export default AccountTabs;
